'use client';

import { useState, useEffect } from 'react';
import { useElectron } from '@/hooks/use-electron';
import { Settings, Minimize, Maximize, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/**
 * Custom Title Bar for Electron Desktop App
 * Provides proper spacing for window controls and draggable area
 */
const CustomTitleBar = () => {
  const { isElectron, platform } = useElectron();
  const [isMaximized, setIsMaximized] = useState(false);

  // Add CSS class to body when title bar is mounted
  useEffect(() => {
    if (isElectron) {
      document.body.classList.add('has-custom-title-bar');
      return () => {
        document.body.classList.remove('has-custom-title-bar');
      };
    }
  }, [isElectron]);

  // Only show title bar in Electron desktop app
  if (!isElectron) return null;

  const isMac = platform === 'darwin';
  const isWindows = platform === 'win32';

  const handleMinimize = () => {
    if (window.electronAPI) {
      window.electronAPI.windowControl?.('minimize');
    }
  };

  const handleMaximize = () => {
    if (window.electronAPI) {
      window.electronAPI.windowControl?.('maximize');
      setIsMaximized(!isMaximized);
    }
  };

  const handleClose = () => {
    if (window.electronAPI) {
      window.electronAPI.windowControl?.('close');
    }
  };

  const handleSettings = (action: string) => {
    switch (action) {
      case 'always-on-top':
        window.electronAPI?.toggleAlwaysOnTop?.();
        break;
      case 'dev-tools':
        window.electronAPI?.toggleDevTools?.();
        break;
      case 'reload':
        window.location.reload();
        break;
      case 'about':
        window.electronAPI?.showAbout?.();
        break;
    }
  };

  return (
    <div
      className="custom-title-bar flex items-center justify-between h-8 px-3 select-none fixed top-0 left-0 right-0"
      style={{
        // backgroundColor: '#88a1ac33',
        // borderBottom: '1px solid rgba(136, 161, 172, 0.2)',
        zIndex: 9999,
        // @ts-ignore - WebkitAppRegion is a valid CSS property for Electron
        WebkitAppRegion: 'drag',
      }}
    >
      {/* Left side - App title/logo area with proper spacing for native controls */}
      {/* <div className="flex items-center">
        <div className={`flex items-center ${isMac ? 'ml-20' : 'ml-2'}`}>
          <img 
            src="/img/logo/nin_logo.png" 
            alt="NIN Logo" 
            className="h-5 w-5 mr-2"
            onError={(e) => {
              // Fallback if logo not found
              e.currentTarget.style.display = 'none';
            }}
          />
          <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
            NINGENIE
          </span>
        </div>
      </div> */}

      {/* Center - Draggable area */}
      <div className="flex-1" />

      {/* Right side - Settings with proper spacing for native controls */}
      <div className="flex items-center" style={{ WebkitAppRegion: 'no-drag' } as any}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 hover:bg-gray-200/50 justify-center items-center"
            >
              <Settings className="h-3 w-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48 bg-[var(--background)]">
            <DropdownMenuItem onClick={() => handleSettings('always-on-top')}>
              Always on Top
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleSettings('reload')}>
              Reload App
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => handleSettings('dev-tools')}>
              Developer Tools
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleSettings('about')}>
              About NIN Terminal
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

export default CustomTitleBar;